import { gql } from "@apollo/client";
import { BaseMutationTuple, jssaQuery, useBaseMutation } from "./index";
import { ShopifyProduct, ShopifyProductVariant } from "../../../common/models";
import { shopifyProductAllFieldsFragment } from "./CommonQueries";
import { ShopifyProductsInput } from "../../../common/inputs/ShopifyProductsInput";

export const shopifyProductsForAdminQuery = gql`
    ${shopifyProductAllFieldsFragment}
    query shopifyProductsForAdmin($options: ShopifyProductsInput!) {
        shopifyProductsForAdmin(options: $options) {
            ...ShopifyProductAllFields
        }
    }
`;

export const shopifyProductByProductListingIdQuery = gql`
    ${shopifyProductAllFieldsFragment}
    query shopifyProductByProductListingId($productListingId: String!) {
        shopifyProductByProductListingId(productListingId: $productListingId) {
            ...ShopifyProductAllFields
        }
    }
`;

export const connectShopifyProductToStoreMutation = gql`
    ${shopifyProductAllFieldsFragment}
    mutation connectShopifyProductToStore($shopifyProductId: BigInt!, $storeId: String!) {
        connectShopifyProductToStore(shopifyProductId: $shopifyProductId, storeId: $storeId) {
            ...ShopifyProductAllFields
        }
    }
`;

export const queryShopifyProductsForAdmin = async (
    options: ShopifyProductsInput,
): Promise<ShopifyProduct[]> => {
    const { data: { shopifyProductsForAdmin } } = await jssaQuery<{ shopifyProductsForAdmin: ShopifyProduct[] }>({
        query: shopifyProductsForAdminQuery,
        variables: {
            options,
        },
    });
    return shopifyProductsForAdmin.map(product => ({
        ...product,
        id: String(product.id),
    }));
};

export const queryShopifyProductByProductListingId = async (
    productListingId: string,
): Promise<ShopifyProduct | null> => {
    const { data: { shopifyProductByProductListingId } } = await jssaQuery<{ shopifyProductByProductListingId: ShopifyProduct | null }>({
        query: shopifyProductByProductListingIdQuery,
        variables: {
            productListingId,
        },
    });
    return shopifyProductByProductListingId;
};

export const variantsWithoutCostMetadataQuery = gql`
    query variantsWithoutCostMetadata($shopifyProductId: String!) {
        variantsWithoutCostMetadata(shopifyProductId: $shopifyProductId) {
            id
            sku
            title
        }
    }
`;

export const queryVariantsWithoutCostMetadata = async (
    shopifyProductId: string,
): Promise<ShopifyProductVariant[]> => {
    const { data: { variantsWithoutCostMetadata } } = await jssaQuery<{ variantsWithoutCostMetadata: ShopifyProductVariant[] }>({
        query: variantsWithoutCostMetadataQuery,
        variables: { shopifyProductId },
    });
    return variantsWithoutCostMetadata.map(variant => ({
        ...variant,
        id: String(variant.id),
    }));
};

export const queryAllVariantsByProductListingId = async (
    productListingId: string,
): Promise<ShopifyProductVariant[]> => {
    const product = await queryShopifyProductByProductListingId(productListingId) as (ShopifyProduct & { variants?: ShopifyProductVariant[] }) | null;
    if (!product?.variants) {
        return [];
    }
    return product.variants.map((variant: ShopifyProductVariant) => ({
        ...variant,
        id: String(variant.id),
    }));
};

export const useConnectShopifyProductToStoreMutation = (): BaseMutationTuple<{ shopifyProductId: string; storeId: string }, ShopifyProduct> => {
    const transformInput = (input: { shopifyProductId: string; storeId: string }) => {
        return {
            shopifyProductId: input.shopifyProductId,
            storeId: input.storeId,
        };
    };

    const transformOutput = (output: any) => {
        return output.connectShopifyProductToStore;
    };

    return useBaseMutation<{ shopifyProductId: string; storeId: string }, ShopifyProduct>(
        connectShopifyProductToStoreMutation,
        transformInput,
        transformOutput,
    );
};
